import { Box, Link, Skeleton, TableCell, TableRow } from '@mui/material';
import PopoverListWithFilter from '@shared/components/others/PopoverListWithFilter/PopoverListWithFilter';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dateFormat, REQUEST_STATUS } from '@shared/constants/otherConstants';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { UI_FUNCTIONAL_TESTING_PATH, UI_TEST_RUN_PATH } from '@shared/constants/pathsConstants';
import TestrunResult from '@pages/testruns/components/TestrunResult';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useTestrunsChunk } from '@pages/testruns/hooks/useTestrunsChunk';
import { getTestRunsRequest } from '@pages/testruns/store';
import { selectGetTestRunsStatus, selectTestRuns } from '@pages/testruns/store/selectors';

export const INITIAL_PAGE_SIZE = 5;

const TestRunsPopover = (props) => (
    <ComponentWithChunks
        featuresArray={['testRuns']}
        hooksArray={[useTestrunsChunk]}
        Component={TestRunsPopoverComponent}
        {...props}
    />
);

const TestRunsPopoverComponent = ({ testrunsPopoverAnchorEl, setTestrunsPopoverAnchorEl, testplanId }) => {
    const { t } = useTranslation();
    const testRuns = useSelector(selectTestRuns);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isLoading = useSelector(selectGetTestRunsStatus) === REQUEST_STATUS.LOADING;
    const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);

    const getTestRuns = useCallback(
        (params) => {
            dispatch(getTestRunsRequest(params));
        },
        [dispatch],
    );

    useEffect(() => {
        if (testrunsPopoverAnchorEl) {
            getTestRuns({ testplan: testplanId, pageSize });
        } else {
            setPageSize(INITIAL_PAGE_SIZE);
        }
    }, [getTestRuns, pageSize, testplanId, testrunsPopoverAnchorEl]);

    return (
        <PopoverListWithFilter
            anchorEl={testrunsPopoverAnchorEl}
            setAnchorEl={setTestrunsPopoverAnchorEl}
            data={testRuns}
            renderValue={(testrun) => {
                let { createdAt } = testrun;
                createdAt = dayjs(createdAt).format(dateFormat);

                return (
                    <TableRow key={testrun.pk}>
                        <TableCell size="small" sx={{ minWidth: 140 }}>
                            <Link
                                underline="hover"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${testrun.pk}`);
                                }}
                                href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${testrun.pk}`}
                                sx={{ cursor: 'pointer', fontSize: 14 }}
                            >
                                {createdAt}
                            </Link>
                        </TableCell>
                        <TableCell size="small">
                            <TestrunResult testrun={testrun} />
                        </TableCell>
                    </TableRow>
                );
            }}
            table={{ columns: [t('testrun_date'), t('results')] }}
            title={t('test_runs')}
            search={false}
            isLoading={isLoading}
            Skeleton={TestRunsSkeleton}
            loadMore
            setPageSize={setPageSize}
        />
    );
};

export default TestRunsPopover;

const TestRunsSkeleton = () => {
    const skeletons = [1, 2, 3, 4, 5];
    return skeletons.map((item) => (
        <TableRow key={item}>
            <TableCell size="small" sx={{ width: 140 }}>
                <Skeleton animation="wave" variant="text" sx={{ fontSize: 14 }} width={83} />
            </TableCell>
            <TableCell size="small">
                <Box sx={{ display: 'flex' }}>
                    <Skeleton variant="circular" animation="wave" width={40} height={40} sx={{ mr: 3 }} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Skeleton variant="rectangular" width={8} height={8} animation="wave" />
                            <Box>
                                <Skeleton variant="text" width={42} animation="wave" sx={{ fontSize: 14 }} />
                                <Skeleton variant="text" width={33} animation="wave" sx={{ fontSize: 14 }} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Skeleton variant="rectangular" width={8} height={8} animation="wave" />
                            <Box>
                                <Skeleton variant="text" width={42} animation="wave" sx={{ fontSize: 14 }} />
                                <Skeleton variant="text" width={33} animation="wave" sx={{ fontSize: 14 }} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Skeleton variant="rectangular" width={8} height={8} animation="wave" />
                            <Box>
                                <Skeleton variant="text" width={42} animation="wave" sx={{ fontSize: 14 }} />
                                <Skeleton variant="text" width={33} animation="wave" sx={{ fontSize: 14 }} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Skeleton variant="rectangular" width={8} height={8} animation="wave" />
                            <Box>
                                <Skeleton variant="text" width={42} animation="wave" sx={{ fontSize: 14 }} />
                                <Skeleton variant="text" width={33} animation="wave" sx={{ fontSize: 14 }} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </TableCell>
        </TableRow>
    ));
};
